import React, { useState } from 'react';
import { useAtom } from 'jotai';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { FieldSelector } from './FieldSelector';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle } from '@/theme';
import { worklogsTemplateFields } from '../../state';
import { abyssColors } from '@/theme/colors';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { keyFromTitle } from '../../utils';
import {
  LogSection,
  SectionUnitsEnum,
  TemplateFieldTypeEnum,
  LogsTemplateField,
} from '@/__generated__/graphql';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type Mode = 'ReadOnly' | 'Edit';

const SECTION_UNIT_OPTIONS = [
  { label: 'USD', value: SectionUnitsEnum.Usd },
  { label: 'GBP', value: SectionUnitsEnum.Gbp },
  { label: 'Hours', value: SectionUnitsEnum.Hours },
  { label: 'Area', value: SectionUnitsEnum.Area },
  { label: 'N/A', value: SectionUnitsEnum.Na },
];

type DragHandleProps = DraggableAttributes & {
  listeners?: DraggableSyntheticListeners;
};

type SectionCardProps = {
  section: LogSection;
  mode: Mode;
  onChange: (section: LogSection) => void;
  onRemove?: (section: LogSection) => void;
  dragHandleProps?: DragHandleProps;
};

const getFieldDescriptionHelperText = (
  fieldDescription: string | null | undefined,
  mode: Mode
): string | undefined => {
  if (mode !== 'Edit') {
    return undefined;
  }

  const description = fieldDescription || '';
  const length = description.length;
  const maxLength = 100;

  if (length > maxLength) {
    return `Description is too long (${length}/${maxLength} characters)`;
  }

  return `${length}/${maxLength} characters`;
};

const DragHandle = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => (
  <IconButton
    size="small"
    sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
    {...dragHandleProps}
  >
    <DragIndicatorIcon />
  </IconButton>
);

export const SectionCard = ({
  section,
  mode,
  onChange,
  onRemove,
  dragHandleProps,
}: SectionCardProps) => {
  const [fields, setFields] = useAtom(worklogsTemplateFields);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const selectedFieldKeys = section.fieldKeys ?? [];
  const allFields = fields ?? [];

  const availableFields = allFields.filter(
    (f) => !selectedFieldKeys.includes(f.key ?? '') && !f.isDerived
  );

  const handleSectionChange = (partial: Partial<LogSection>) => {
    onChange({ ...section, ...partial });
  };

  const handleSelectExistingField = (fieldKey: string) => {
    handleSectionChange({
      fieldKeys: [...selectedFieldKeys, fieldKey],
    });
  };

  const handleCreateNewField = (title: string, description?: string) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    const key = keyFromTitle(trimmedTitle);

    const newField: LogsTemplateField = {
      key,
      name: trimmedTitle,
      type: TemplateFieldTypeEnum.Input,
      sectionKey: key,
      options: [],
      isReadOnly: false,
      isRequired: false,
      serverSide: false,
      id: generateMongoObjectId(),
      fieldDescription: description || null,
    };

    const updatedFields = [...allFields, newField];
    setFields(updatedFields);

    handleSectionChange({
      fieldKeys: [...selectedFieldKeys, key],
    });
  };

  const validateNewFieldName = (name: string): string => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return 'Field name is required';
    }
    if (trimmedName.length < 2) {
      return 'Field name must be at least 2 characters';
    }

    const key = keyFromTitle(trimmedName);
    const existingField = allFields.find((field) => field.key === key);
    if (existingField) {
      return `Field with key "${key}" already exists. Please use a different name.`;
    }

    return '';
  };

  const handleUpdateFieldDescription = (fieldKey: string, description: string) => {
    const updatedFields = allFields.map((field) => {
      if (field.key === fieldKey) {
        return { ...field, fieldDescription: description || '' };
      }
      return field;
    });
    setFields(updatedFields);
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              {mode === 'Edit' && <DragHandle dragHandleProps={dragHandleProps} />}
              {mode === 'Edit' && onRemove && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  sx={{ '&:hover': { backgroundColor: 'error.light' } }}
                >
                  <DeleteIcon />
                </IconButton>
              )}

              <Stack direction="row" spacing={2} flex={1}>
                <Box flex={2}>
                  {mode === 'Edit' ? (
                    <TextField
                      fullWidth
                      label="Title"
                      value={section.title}
                      onChange={(e) => handleSectionChange({ title: e.target.value })}
                      onBlur={() => {
                        handleSectionChange({
                          key: keyFromTitle(section.title),
                        });
                      }}
                    />
                  ) : (
                    <Typography variant="h4">{section.title}</Typography>
                  )}
                </Box>

                <Box flex={1}>
                  <TextField fullWidth label="Key" disabled value={section.key} />
                </Box>
              </Stack>
            </Stack>

            <FormControl fullWidth size="small">
              <InputLabel>Section Unit</InputLabel>
              <Select
                label="Section Unit"
                value={section.sectionUnit ?? ''}
                disabled={mode === 'ReadOnly'}
                onChange={(e) =>
                  handleSectionChange({
                    sectionUnit: e.target.value as SectionUnitsEnum,
                  })
                }
              >
                {SECTION_UNIT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!section.defaultOpen}
                  disabled={mode === 'ReadOnly'}
                  onChange={(e) =>
                    handleSectionChange({
                      defaultOpen: e.target.checked,
                    })
                  }
                />
              }
              label="Default Open"
            />

            <Box>
              <Typography variant="subtitle2">Fields</Typography>
              {selectedFieldKeys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No fields selected
                </Typography>
              ) : (
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;

                    const oldIndex = selectedFieldKeys.indexOf(active.id as string);
                    const newIndex = selectedFieldKeys.indexOf(over.id as string);

                    if (oldIndex === -1 || newIndex === -1) return;

                    const reorderedKeys = arrayMove(selectedFieldKeys, oldIndex, newIndex);
                    handleSectionChange({ fieldKeys: reorderedKeys });
                  }}
                >
                  <SortableContext
                    items={selectedFieldKeys}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedFieldKeys.map((key) => {
                        const field = allFields.find((f) => f.key === key);
                        return (
                          <SortableFieldChip
                            key={key}
                            id={key}
                            label={field?.name ?? key}
                            disabled={mode === 'ReadOnly'}
                            onRemove={() => {
                              const updatedKeys = selectedFieldKeys.filter((k) => k !== key);
                              handleSectionChange({ fieldKeys: updatedKeys });
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </SortableContext>
                </DndContext>
              )}
            </Box>

            {mode === 'Edit' && (
              <FieldSelector
                availableFields={availableFields}
                onSelectExisting={handleSelectExistingField}
                onCreateNew={handleCreateNewField}
                validateNewFieldName={validateNewFieldName}
              />
            )}

            {selectedFieldKeys.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Field Descriptions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {selectedFieldKeys.map((key) => {
                      const field = allFields.find((f) => f.key === key);
                      if (!field) return null;

                      return (
                        <TextField
                          key={key}
                          label={`Description for "${field.name}"`}
                          value={field.fieldDescription || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            // Only allow updates if under character limit or if deleting characters
                            if (newValue.length <= 100) {
                              handleUpdateFieldDescription(field.key!, newValue);
                            }
                          }}
                          multiline
                          rows={2}
                          placeholder={
                            mode === 'Edit' ? 'Enter field description (optional)' : undefined
                          }
                          size="small"
                          disabled={mode === 'ReadOnly'}
                          error={mode === 'Edit' && (field.fieldDescription || '').length > 100}
                          helperText={getFieldDescriptionHelperText(field.fieldDescription, mode)}
                          slotProps={{
                            input: {
                              readOnly: mode === 'ReadOnly',
                              inputProps: {
                                maxLength: 100,
                              },
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </CardContent>
      </Card>
      <GenericDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Section"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            Are you sure you want to delete this section?
          </Typography>
        }
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setIsDeleteDialogOpen(false)}
              sx={{ color: abyssColors.primary[500] }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onRemove?.(section);
                setIsDeleteDialogOpen(false);
              }}
              sx={containedButtonStyle}
            >
              Delete
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </>
  );
};

const SortableFieldChip = ({
  id,
  label,
  disabled,
  onRemove,
}: {
  id: string;
  label: string;
  disabled: boolean;
  onRemove?: () => void;
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <Chip
        ref={setNodeRef}
        style={style}
        label={
          <Box sx={{ cursor: disabled ? 'default' : 'grab' }} {...attributes} {...listeners}>
            {label}
          </Box>
        }
        size="small"
        deleteIcon={
          !disabled && onRemove ? (
            <CloseIcon
              sx={{
                color: abyssColors.neutral[100],
                '&:hover': {
                  color: abyssColors.neutral[200],
                },
              }}
            />
          ) : undefined
        }
        onDelete={
          !disabled && onRemove
            ? (e) => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }
            : undefined
        }
        sx={{
          color: abyssColors.neutral[100],
          backgroundColor: abyssColors.primary[400],
          '& .MuiChip-deleteIcon': {
            color: abyssColors.neutral[100],
            '&:hover': {
              color: abyssColors.neutral[200],
            },
          },
        }}
      />
      {showDeleteDialog && (
        <GenericDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Delete Field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this field?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setShowDeleteDialog(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={() => onRemove?.()} sx={containedButtonStyle}>
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
